
import type { KeycloakInitOptions } from 'keycloak-js';
import { keycloakInstance } from './keycloak';
import { useAuthStore } from './auth-store';
import { config } from '@/shared/lib/config';
import { usersApi } from '@/shared/api/users';
import { canUseSilentSso } from './silent-sso-probe';
import { shouldRedirectCheckSso, ssoReturnPathFor } from './sso-redirect-check';
import { isSessionDead } from './refresh-failure';
import { bffLogin, bffLoginFresh, bffLogout, runBffInit } from './bff-auth';
import { isAuthlessPath } from './authless-paths';
import { rememberReturnPath, takeReturnPath } from './return-path';
import { fallBackToKeycloak, isBffMode } from './auth-mode';

let initPromise: Promise<boolean> | null = null;

const TOKEN_KEY = 'keycloak_token';
const REFRESH_KEY = 'keycloak_refresh_token';

function readStored(key: string): string | null {
  try {
    return sessionStorage.getItem(key) ?? localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
    localStorage.setItem(key, value);
  } catch {
  }
}

function removeStored(key: string): void {
  try {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  } catch {
  }
}

function sessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function sessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
  }
}

function sessionRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
  }
}

const SSO_CHECK_AT_KEY = 'keycloak_sso_check_at';
export function takeSsoReturnPath(): string | null {
  return takeReturnPath();
}

function persistTokens(): void {
  if (keycloakInstance.token) writeStored(TOKEN_KEY, keycloakInstance.token);
  if (keycloakInstance.refreshToken) writeStored(REFRESH_KEY, keycloakInstance.refreshToken);
}

function clearTokens(): void {
  removeStored(TOKEN_KEY);
  removeStored(REFRESH_KEY);
}

function getGroupsFromToken(token: { realm_access?: { roles?: string[] }; groups?: string[] }): string[] {
  if (token.groups && Array.isArray(token.groups)) {
    return token.groups;
  }
  if (token.realm_access?.roles && Array.isArray(token.realm_access.roles)) {
    return token.realm_access.roles;
  }
  return [];
}

const TOKEN_RESTORE_BUFFER_SEC = 30;

const TOKEN_MIN_VALIDITY = 30;

const REFRESH_INTERVAL_MS = 60_000;
const REFRESH_MIN_VALIDITY_SEC = 70;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

let transientRefreshFailures = 0;

function handleRefreshFailure(): void {
  transientRefreshFailures += 1;
  if (!isSessionDead(keycloakInstance.refreshToken, transientRefreshFailures)) return;
  transientRefreshFailures = 0;
  stopRefreshTimer();
  useAuthStore.getState().logout();
  handleUnauthorized();
}

function startRefreshTimer(): void {
  if (refreshTimer !== null) return;
  refreshTimer = setInterval(() => {
    keycloakInstance
      .updateToken(REFRESH_MIN_VALIDITY_SEC)
      .then((refreshed) => {
        transientRefreshFailures = 0;
        if (refreshed) persistTokens();
      })
      .catch(handleRefreshFailure);
  }, REFRESH_INTERVAL_MS);
}

function stopRefreshTimer(): void {
  if (refreshTimer !== null) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isTokenNotExpired(token: string, bufferSec = TOKEN_RESTORE_BUFFER_SEC): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp as number | undefined;
  if (!exp) return false;
  return Date.now() / 1000 < exp - bufferSec;
}

type AuthProfile = {
  id?: string;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
};

function logAuthDebug(
  user: { id: string; email: string; name: string; roles: string[] },
  tokenParsed: Record<string, unknown> | undefined
) {
  if (!import.meta.env.DEV) return;
  const safePayload = tokenParsed
    ? {
        sub: tokenParsed.sub,
        email: tokenParsed.email ?? tokenParsed.preferred_username,
        preferred_username: tokenParsed.preferred_username,
        given_name: tokenParsed.given_name,
        family_name: tokenParsed.family_name,
        realm_access: tokenParsed.realm_access,
        groups: tokenParsed.groups,
        exp: tokenParsed.exp ? new Date((tokenParsed.exp as number) * 1000).toISOString() : undefined,
        iat: tokenParsed.iat ? new Date((tokenParsed.iat as number) * 1000).toISOString() : undefined,
      }
    : null;
  console.log('[Auth Debug] User:', user);
  console.log('[Auth Debug] Token payload (safe):', safePayload);
}

const ME_BOOT_TIMEOUT_MS = 6000;

async function applyAuthenticated(syncFromKeycloak: ReturnType<typeof useAuthStore.getState>['syncFromKeycloak']): Promise<void> {
  persistTokens();

  const tokenParsed = keycloakInstance.tokenParsed as
    | { sub?: string; email?: string; preferred_username?: string; given_name?: string; family_name?: string }
    | undefined;
  if (!tokenParsed) return;

  const groups = getGroupsFromToken(keycloakInstance.tokenParsed as Parameters<typeof getGroupsFromToken>[0]);
  const profile: AuthProfile = {
    id: tokenParsed.sub,
    email: tokenParsed.email ?? tokenParsed.preferred_username,
    username: tokenParsed.preferred_username,
    firstName: tokenParsed.given_name,
    lastName: tokenParsed.family_name,
  };
  syncFromKeycloak(profile, groups);

  const applyMe = usersApi
    .getMe()
    .then((res) => {
      const me = (res as { data?: { id?: string; email?: string; firstName?: string | null; lastName?: string | null; roles?: string[]; permissions?: string[]; avatarUrl?: string | null; wikiAccess?: boolean; toursSeen?: Record<string, number> } }).data;
      if (me?.roles?.length || Array.isArray(me?.permissions)) {
        syncFromKeycloak(
          {
            ...profile,
            id: me.id ?? profile.id,
            email: me.email ?? profile.email,
            firstName: me.firstName ?? profile.firstName,
            lastName: me.lastName ?? profile.lastName,
            avatarUrl: me.avatarUrl ?? null,
            wikiAccess: me.wikiAccess,
            toursSeen: me.toursSeen,
          },
          groups,
          me.roles?.length ? (me.roles as import('@/shared/config').AppRole[]) : undefined,
          me.permissions
        );
      }
    })
    .catch(() => {
    });

  await Promise.race([
    applyMe,
    new Promise<void>((resolve) => setTimeout(resolve, ME_BOOT_TIMEOUT_MS)),
  ]);

  const authUser = useAuthStore.getState().user;
  if (authUser) logAuthDebug(authUser, keycloakInstance.tokenParsed as Record<string, unknown> | undefined);

  keycloakInstance.onTokenExpired = () => {
    keycloakInstance
      .updateToken(TOKEN_MIN_VALIDITY)
      .then((refreshed) => {
        transientRefreshFailures = 0;
        if (refreshed) persistTokens();
      })
      .catch(handleRefreshFailure);
  };

  startRefreshTimer();
}

async function runKeycloakInit(): Promise<boolean> {
  const { setInitialized, setError, syncFromKeycloak } = useAuthStore.getState();

  const keycloakUrl = config.keycloak.url || 'http://localhost:8080';
  if (keycloakUrl === 'http://localhost:8080') {
    console.warn('Keycloak URL not configured (using default). Set VITE_KEYCLOAK_URL for production.');
  }

  try {
    const redirectUri = config.keycloak.redirectUri || window.location.origin;
    const storedToken = readStored(TOKEN_KEY) ?? undefined;
    const storedRefresh = readStored(REFRESH_KEY) ?? undefined;

    const silentCheckSsoRedirectUri = `${window.location.origin}/silent-check-sso.html`;
    const silentSsoAvailable = await canUseSilentSso(silentCheckSsoRedirectUri);
    if (!silentSsoAvailable) {
      console.warn(
        '[Keycloak] Проверка SSO в iframe недоступна: silent-check-sso.html ' +
          'отдаётся с заголовками, при которых iframe не ответит ' +
          '(X-Frame-Options/CSP). Подхватываем сессию полным редиректом ' +
          'с prompt=none.',
      );
    }

    const initOptions: KeycloakInitOptions = {
      checkLoginIframe: false,
      redirectUri,
      pkceMethod: 'S256',
    };

    const refreshAlive = storedRefresh ? isTokenNotExpired(storedRefresh, 0) : false;
    const hasStoredSession = Boolean(storedToken && storedRefresh && refreshAlive);
    if (storedToken && storedRefresh && refreshAlive) {
      initOptions.token = storedToken;
      initOptions.refreshToken = storedRefresh;
    } else if (storedToken || storedRefresh) {
      clearTokens();
    }

    if (silentSsoAvailable) {
      initOptions.onLoad = 'check-sso';
      initOptions.silentCheckSsoRedirectUri = silentCheckSsoRedirectUri;
      initOptions.messageReceiveTimeout = 2000;
    } else if (
      shouldRedirectCheckSso({
        search: window.location.search,
        hash: window.location.hash,
        revoked: sessionGet('session_revoked') === '1',
        hasStoredSession,
        lastCheckAt: Number(sessionGet(SSO_CHECK_AT_KEY) ?? 0),
        now: Date.now(),
      })
    ) {
      initOptions.onLoad = 'check-sso';
      sessionSet(SSO_CHECK_AT_KEY, String(Date.now()));
      const returnTo = ssoReturnPathFor(
        window.location.pathname,
        window.location.search,
        window.location.hash,
      );
      if (returnTo) rememberReturnPath(returnTo);
    }

    await keycloakInstance.init(initOptions);

    if (keycloakInstance.authenticated && keycloakInstance.token) {
      try {
        const refreshed = await keycloakInstance.updateToken(TOKEN_MIN_VALIDITY);
        if (refreshed) persistTokens();
      } catch {
        if (isSessionDead(keycloakInstance.refreshToken, 0)) {
          clearTokens();
          useAuthStore.getState().setAuthenticated(false);
          setInitialized(true);
          return false;
        }
        const stillValid = keycloakInstance.token ? isTokenNotExpired(keycloakInstance.token, 0) : false;
        if (!stillValid) {
          useAuthStore.getState().setAuthenticated(false);
          setInitialized(true);
          return false;
        }
      }
      await applyAuthenticated(syncFromKeycloak);
    } else {
      clearTokens();
      useAuthStore.getState().setAuthenticated(false);
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Auth initialization failed';
    console.error('[Keycloak]', msg, err);
    setError(msg);
    useAuthStore.getState().setAuthenticated(false);
  }

  setInitialized(true);
  return useAuthStore.getState().isAuthenticated;
}

export function initKeycloak(): Promise<boolean> {
  if (initPromise !== null) {
    return initPromise;
  }
  initPromise = isBffMode()
    ? runBffInit().then((r) => {
        if (r !== 'fallback') return r;
        fallBackToKeycloak();
        return runKeycloakInit();
      })
    : runKeycloakInit();
  return initPromise;
}

export async function getValidToken(forceRefresh = false): Promise<string | null> {
  if (isBffMode()) return null;
  const minValidity = forceRefresh ? -1 : TOKEN_MIN_VALIDITY;
  if (!keycloakInstance.authenticated) {
    const stored = readStored(TOKEN_KEY);
    if (stored && isTokenNotExpired(stored, 0)) return stored;
    if (stored) clearTokens();
    return null;
  }
  try {
    const refreshed = await keycloakInstance.updateToken(minValidity);
    if (refreshed) persistTokens();
    return keycloakInstance.token ?? readStored(TOKEN_KEY);
  } catch {
    const stored = readStored(TOKEN_KEY);
    if (stored && isTokenNotExpired(stored, 0)) return stored;
    if (isSessionDead(keycloakInstance.refreshToken, 0)) clearTokens();
    return null;
  }
}

export function isSessionAlive(): boolean {
  if (isBffMode()) return useAuthStore.getState().isAuthenticated;
  return Boolean(keycloakInstance.authenticated) && !isSessionDead(keycloakInstance.refreshToken, 0);
}

function getRedirectUri() {
  return config.keycloak.redirectUri || window.location.origin;
}

export function login() {
  if (isBffMode()) return bffLogin();
  const redirectUri = getRedirectUri();
  keycloakInstance.login({ redirectUri });
}

export function loginFresh() {
  if (isBffMode()) return bffLoginFresh();
  sessionRemove('session_revoked');
  const redirectUri = getRedirectUri();
  keycloakInstance.login({ redirectUri, prompt: 'login' });
}

export function keycloakLogout() {
  if (isBffMode()) {
    void bffLogout();
    return;
  }
  if (import.meta.env.DEV) console.log('[Auth Debug] Logout initiated');
  stopRefreshTimer();
  clearTokens();
  sessionSet(SSO_CHECK_AT_KEY, String(Date.now()));
  useAuthStore.getState().logout();
  const redirectUri = getRedirectUri();
  keycloakInstance.logout({ redirectUri });
}

let unauthorizedRedirectStarted = false;

export function handleUnauthorized() {
  stopRefreshTimer();
  clearTokens();
  useAuthStore.getState().logout();
  if (isAuthlessPath()) return;
  if (unauthorizedRedirectStarted) return;
  if (window.location.pathname === '/login') return;
  unauthorizedRedirectStarted = true;
  window.location.assign('/login');
}

export function handleSessionRevoked() {
  stopRefreshTimer();
  clearTokens();
  sessionSet('session_revoked', '1');
  useAuthStore.getState().logout();
  if (isAuthlessPath()) return;
  if (unauthorizedRedirectStarted) return;
  unauthorizedRedirectStarted = true;
  window.location.assign('/login');
}
